/**
 * Script Loader / Tag Manager
 * Conditionally loads tracking scripts based on consent
 */

import { trackingConfig } from './consent-config';
import { ConsentPreferences, loadConsentState } from './consent-store';

type ScriptStatus = 'idle' | 'loading' | 'loaded' | 'error';

const scriptStatus: Record<string, ScriptStatus> = {};

// Load script dynamically
const loadScript = (
  id: string,
  src: string,
  onLoad?: () => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (scriptStatus[id] === 'loaded') {
      onLoad?.();
      resolve();
      return;
    }
    
    if (scriptStatus[id] === 'loading') {
      // Wait for existing load
      const checkLoaded = setInterval(() => {
        if (scriptStatus[id] === 'loaded') {
          clearInterval(checkLoaded);
          onLoad?.();
          resolve();
        } else if (scriptStatus[id] === 'error') {
          clearInterval(checkLoaded);
          reject(new Error(`Script ${id} failed to load`));
        }
      }, 100);
      return;
    }
    
    scriptStatus[id] = 'loading';
    
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    
    script.onload = () => {
      scriptStatus[id] = 'loaded';
      onLoad?.();
      resolve();
    };
    
    script.onerror = () => {
      scriptStatus[id] = 'error';
      reject(new Error(`Failed to load script: ${src}`));
    };
    
    document.head.appendChild(script);
  });
};

// Load inline script
const loadInlineScript = (id: string, code: string): void => {
  if (document.getElementById(id)) return;
  
  const script = document.createElement('script');
  script.id = id;
  script.textContent = code;
  document.head.appendChild(script);
};

// Google Analytics 4
export const loadGA4 = async (): Promise<void> => {
  if (!trackingConfig.ga4.enabled) return;
  
  const measurementId = trackingConfig.ga4.measurementId;
  if (measurementId.startsWith('G-XXXX')) {
    console.warn('GA4: Using placeholder measurement ID');
    return;
  }
  
  await loadScript(
    'ga4-script',
    `https://www.googletagmanager.com/gtag/js?id=${measurementId}`,
    () => {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function(...args) {
        window.dataLayer.push(args);
      };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false, // We'll send manually
      });
    }
  );
};

// Meta Pixel
export const loadMetaPixel = (): void => {
  if (!trackingConfig.meta.enabled) return;
  
  const pixelId = trackingConfig.meta.pixelId;
  if (pixelId.startsWith('XXXXX')) {
    console.warn('Meta Pixel: Using placeholder pixel ID');
    return;
  }
  
  loadInlineScript('meta-pixel', `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
  `);
};

// TikTok Pixel
export const loadTikTokPixel = (): void => {
  if (!trackingConfig.tiktok.enabled) return;
  
  const pixelId = trackingConfig.tiktok.pixelId;
  if (pixelId.startsWith('XXXXX')) {
    console.warn('TikTok Pixel: Using placeholder pixel ID');
    return;
  }
  
  loadInlineScript('tiktok-pixel', `
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
      ttq.load('${pixelId}');
      ttq.page();
    }(window, document, 'ttq');
  `);
};

// LinkedIn Insight Tag
export const loadLinkedInTag = (): void => {
  if (!trackingConfig.linkedin.enabled) return;
  
  const partnerId = trackingConfig.linkedin.partnerId;
  if (partnerId.startsWith('XXXXX')) {
    console.warn('LinkedIn: Using placeholder partner ID');
    return;
  }
  
  window._linkedin_partner_id = partnerId;
  
  loadInlineScript('linkedin-tag', `
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push('${partnerId}');
    (function(l) {
      if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
      window.lintrk.q=[]}
      var s = document.getElementsByTagName("script")[0];
      var b = document.createElement("script");
      b.type = "text/javascript";b.async = true;
      b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
      s.parentNode.insertBefore(b, s);})(window.lintrk);
  `);
};

// Microsoft/Bing Ads UET
export const loadBingTag = (): void => {
  if (!trackingConfig.bing.enabled) return;
  
  const tagId = trackingConfig.bing.tagId;
  if (tagId.startsWith('XXXXX')) {
    console.warn('Bing: Using placeholder tag ID');
    return;
  }
  
  loadInlineScript('bing-tag', `
    (function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"${tagId}"};o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");
  `);
};

// Load all scripts based on consent
export const loadTrackingScripts = (preferences?: ConsentPreferences): void => {
  const state = preferences ? { preferences } : loadConsentState();
  const prefs = state.preferences || { analytics: false, marketing: false };
  
  // Analytics scripts
  if (prefs.analytics) {
    loadGA4().catch(console.error);
  }
  
  // Marketing scripts
  if (prefs.marketing) {
    loadMetaPixel();
    loadTikTokPixel();
    loadLinkedInTag();
    loadBingTag();
  }
};

// Unload scripts (remove from DOM)
export const unloadTrackingScripts = (): void => {
  const scriptIds = [
    'ga4-script',
    'meta-pixel',
    'tiktok-pixel',
    'linkedin-tag',
    'bing-tag',
  ];
  
  scriptIds.forEach(id => {
    const script = document.getElementById(id);
    if (script) {
      script.remove();
      scriptStatus[id] = 'idle';
    }
  });
};
